import { Dispatch, SetStateAction, useEffect } from 'react'
import { useDispatch, useSelector } from 'store/store'
import { Table, LoadingHavaianas, Pagination, HeadItemType } from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import {
  getTracking,
  resetTrackingPagination,
  setTrackingPage
} from 'store/BackofficeContext/backofficeSlice'
import {
  ColoredText,
  LoadingWrapper,
  ValueRow,
  ValueDivider
} from 'container/BackofficeRanges/styles'
import { theme } from 'styles/theme/hav-theme'
import { numberToDecimalString } from 'commons/utils/number'
import { formatDate } from 'commons/utils/date'

interface TrackingModalProps {
  setOpen: Dispatch<SetStateAction<boolean>>
}

const TrackingModal = ({ setOpen }: TrackingModalProps) => {
  const dispatch = useDispatch()
  const {
    loadingTracking,
    trackingData,
    trackingPag: { totalPages, totalItems, currentPage, pageSize }
  } = useSelector((state) => state.backoffice)

  const rangeStatus = {
    approved: 100,
    pending: 20,
    analysis: 30,
    rejected: 90
  }

  const getStatusColor = (status: number) => {
    if (status === rangeStatus.approved) return theme.colors.alert.success[40]
    if ([rangeStatus.pending, rangeStatus.analysis].includes(status)) {
      return theme.colors.alert.warning[40]
    }
    if (status === rangeStatus.rejected) return theme.colors.alert.danger[40]
    return theme.colors.neutral.high
  }

  const tableLabels: HeadItemType[] = [
    { label: 'Data', columnName: 'Data' },
    { label: 'Hora', columnName: 'Hora' },
    { label: 'Usuário', columnName: 'Usuario' },
    { label: 'Status', columnName: 'Status' },
    { label: 'Regional', columnName: 'Regional' },
    { label: 'Alteração', columnName: 'Alteracao' },
    { label: 'Justificativa', columnName: 'Justificativa' }
  ]

  const toDec = numberToDecimalString

  const tableData = () => {
    if (!trackingData.length) return []

    return trackingData.map((item) => {
      const renderChange = () => {
        if (item.PersentualSugerido !== item.PersentualVigente) {
          const percent = `${toDec(item.PersentualVigente)}% ->
          ${toDec(item.PersentualSugerido)}%`
          if (item.ValorSugerido !== item.ValorVigente) {
            const currency = `R$ ${toDec(item.ValorVigente)} ->
            R$ ${toDec(item.ValorSugerido)}`
            return (
              <ColoredText color={getStatusColor(item.Status.Codigo)}>
                <ValueRow>
                  {percent}
                  <ValueDivider />
                  {currency}
                </ValueRow>
              </ColoredText>
            )
          }
          return (
            <ColoredText color={getStatusColor(item.Status.Codigo)}>
              {percent}
            </ColoredText>
          )
        } else if (item.ValorSugerido !== item.ValorVigente) {
          return (
            <ColoredText color={getStatusColor(item.Status.Codigo)}>
              {`R$ ${toDec(item.ValorVigente)} -> R$ ${toDec(item.ValorSugerido)}`}
            </ColoredText>
          )
        }
        return 'Nehuma alteração'
      }

      return {
        id: item.DataCadastro,
        content: [
          formatDate(item.DataCadastro, 'DD/MM/YYYY'),
          formatDate(item.DataCadastro, 'HH:mm'),
          item.UsuarioCadastro,
          <ColoredText
            color={getStatusColor(item.Status.Codigo)}
            key={`status-${item.DataCadastro}`}
          >
            {item.Status.Descricao}
          </ColoredText>,
          `${item.CodigoEquipe} - ${item.NomeEquipe}`,
          renderChange(),
          item.Observacao ?? ''
        ]
      }
    })
  }

  const handleClose = () => {
    dispatch(resetTrackingPagination())
    setOpen(false)
  }

  const handlePageChange = (page: number) => {
    dispatch(setTrackingPage(page))
    dispatch(getTracking(page, pageSize))
  }

  useEffect(() => {
    dispatch(getTracking(1, pageSize))
  }, [])

  return (
    <ModalContentLayout
      title="Tracking"
      titleSize={22}
      width={'95vw'}
      closeButton
      description="Acompanhe todas as atualizações realizadas neste pedido."
      closeAction={handleClose}
    >
      {loadingTracking ? (
        <LoadingWrapper>
          <LoadingHavaianas />
        </LoadingWrapper>
      ) : (
        <Table headData={tableLabels} bodyData={tableData()} rowHeight={40} fixedHead />
      )}
      <div>
        <Pagination
          disabled={loadingTracking}
          numberOfPages={totalPages}
          currentPage={currentPage}
          numberOfItems={totalItems}
          itemsPerPage={pageSize}
          setCurrentPage={handlePageChange}
        />
      </div>
    </ModalContentLayout>
  )
}

export default TrackingModal
